// Shared mappings for SetOperatorBranches' separator badge + the
// SetOperatorKindDropdown: badge class per render-side `SetOpKind`, and the
// per-engine dropdown choice list (kind + ALL flag + display label) per
// §2.3 S7 decision 1.
//
// Splitting this out keeps SetOperatorBranches.tsx + SetOperatorKindDropdown.tsx
// react-refresh-friendly (component files only export components) and keeps
// the 200-line budget per file honest — mirrors `joinKindMaps.ts`.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — set-operator switch (§2.3 S7).

import type { SqlEngine } from '../../../../sql-grammar/model';
import type { SetOpKind } from './extractSetOperatorBranches';

export const KIND_BADGE_CLASS: Record<SetOpKind, string> = {
  UNION:
    'bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-800',
  INTERSECT:
    'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-800',
  EXCEPT:
    'bg-orange-50 text-orange-800 border-orange-200 dark:bg-orange-900/40 dark:text-orange-200 dark:border-orange-800',
  // Oracle MINUS is EXCEPT's synonym — share the orange badge.
  MINUS:
    'bg-orange-50 text-orange-800 border-orange-200 dark:bg-orange-900/40 dark:text-orange-200 dark:border-orange-800',
};

/**
 * One selectable dropdown entry: kind + ALL modifier + display label + a
 * stable testid suffix (`vqe-set-operator-bar-{i}-kind-option-{optionId}`).
 * UNION and UNION ALL are two separate choices sharing `kind: 'UNION'` so the
 * planner's `all` flag is threaded straight from the picked choice.
 */
export type SetOperatorChoice = {
  readonly kind: SetOpKind;
  readonly all: boolean;
  readonly label: string;
  readonly optionId: string;
};

const UNIVERSAL_CHOICES: readonly SetOperatorChoice[] = [
  { kind: 'UNION', all: false, label: 'UNION', optionId: 'union' },
  { kind: 'UNION', all: true, label: 'UNION ALL', optionId: 'union-all' },
  { kind: 'INTERSECT', all: false, label: 'INTERSECT', optionId: 'intersect' },
  { kind: 'EXCEPT', all: false, label: 'EXCEPT', optionId: 'except' },
];

const ORACLE_CHOICES: readonly SetOperatorChoice[] = [
  { kind: 'UNION', all: false, label: 'UNION', optionId: 'union' },
  { kind: 'UNION', all: true, label: 'UNION ALL', optionId: 'union-all' },
  { kind: 'INTERSECT', all: false, label: 'INTERSECT', optionId: 'intersect' },
  { kind: 'MINUS', all: false, label: 'MINUS', optionId: 'minus' },
];

/**
 * Per-engine dropdown option list (§2.3 S7 decision 1). No option is ever
 * disabled per dialect — e.g. MySQL <8.0.31 lacking INTERSECT/EXCEPT is an
 * accepted run-time error, not a UI gate. Oracle swaps EXCEPT for MINUS (the
 * universally-valid Oracle spelling; EXCEPT needs 21c+) — every other engine
 * offers EXCEPT instead. `INTERSECT`/`EXCEPT`/`MINUS` never carry an ALL
 * variant — none of the five engines define one; only UNION does.
 */
export function setOperatorChoicesForEngine(engine: SqlEngine): readonly SetOperatorChoice[] {
  return engine === 'oracle' ? ORACLE_CHOICES : UNIVERSAL_CHOICES;
}
